import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Video, Save, Clock } from 'lucide-react';
import { getUserVideos, getUserCategories, timestampToString } from '@/lib/firebase';
import { useAuth } from '@/contexts/auth-context';
import { Card, CardContent } from '@/components/ui/card';
import { logger } from '@/lib/logger';

interface CalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDateSelect: (date: string) => void;
  selectedDate?: string;
}

interface VideoCount {
  [date: string]: number;
}

interface ScheduledVideo {
  id: string;
  title: string;
  channel: string;
  status: string;
  publishDate: string;
  contentCategories: string[];
  modules?: string[];
}

export default function CalendarModal({ isOpen, onClose, onDateSelect, selectedDate }: CalendarModalProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [videoCounts, setVideoCounts] = useState<VideoCount>({});
  const [allVideos, setAllVideos] = useState<ScheduledVideo[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [tempSelectedDate, setTempSelectedDate] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (isOpen && user) {
      loadVideoCounts();
      setTempSelectedDate(selectedDate || '');
      // Update current date when modal opens with a selected date
      if (selectedDate) {
        setCurrentDate(new Date(selectedDate));
      } else {
        setCurrentDate(new Date());
      }
    }
  }, [isOpen, user, selectedDate]);

  const loadVideoCounts = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Load both videos and categories
      const [videos, userCategories] = await Promise.all([
        getUserVideos(user.uid),
        getUserCategories(user.uid)
      ]);
      
      setCategories(userCategories);
      
      // Store all videos and count by publish date
      const scheduledVideos = videos
        .filter((video: any) => video.publishDate && video.status !== 'Draft')
        .map((video: any) => ({
          id: video.id,
          title: video.title,
          channel: video.channel || 'Unknown',
          status: video.status,
          publishDate: typeof video.publishDate === 'string' ? video.publishDate : timestampToString(video.publishDate),
          contentCategories: video.contentCategories || [],
          modules: video.modules || []
        }));
      
      setAllVideos(scheduledVideos);
      
      const counts: VideoCount = {};
      scheduledVideos.forEach((video) => {
        const date = video.publishDate;
        counts[date] = (counts[date] || 0) + 1;
      });
      
      setVideoCounts(counts);
    } catch (error) {
      logger.error('Error loading video counts:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const formatDate = (date: Date) => {
    // Use local timezone to avoid UTC conversion issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleDateClick = (date: Date) => {
    const dateString = formatDate(date);
    setTempSelectedDate(dateString);
  };

  const handleSaveDate = () => {
    if (tempSelectedDate) {
      onDateSelect(tempSelectedDate);
      onClose();
    }
  };

  const getVideosForDate = (date: string) => {
    return allVideos.filter(video => video.publishDate === date);
  };

  const getModulesForVideo = (video: ScheduledVideo) => {
    // First check if video has direct modules field
    if (video.modules && video.modules.length > 0) {
      return Array.from(new Set(video.modules));
    }
    
    // Fall back to mapping from contentCategories using tag field
    if (!video.contentCategories || video.contentCategories.length === 0) {
      return ['None'];
    }
    
    // Map category tags to modules (not IDs)
    const videoModules = video.contentCategories
      .map(categoryTag => {
        const category = categories.find(cat => cat.tag === categoryTag);
        return category?.module;
      })
      .filter(Boolean);
    
    if (videoModules.length === 0) {
      return ['None'];
    }
    
    // Return unique modules
    return Array.from(new Set(videoModules));
  };

  const getModuleColor = (module: string) => {
    switch (module?.toLowerCase()) {
      case 'body':
        return 'bg-[#d059c4]/10 text-[#d059c4] border-[#d059c4]';
      case 'mind':
        return 'bg-[#66cee7]/10 text-[#66cee7] border-[#66cee7]';
      case 'general':
        return 'bg-[#595ebe]/10 text-[#595ebe] border-[#595ebe]';
      case 'mixed':
        return 'bg-purple-100 text-purple-600 border-purple-300';
      case 'none':
        return 'bg-gray-100 text-gray-600 border-gray-300';
      default:
        return 'bg-slate-100 text-slate-600 border-slate-300';
    }
  };

  const days = getDaysInMonth(currentDate);
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to avoid timezone issues
  const todayString = formatDate(today);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-sm max-h-[85vh] p-4 flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center space-x-2">
            <CalendarIcon size={20} />
            <span>Select Publish Date</span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-3 flex-1 min-h-0 overflow-hidden">
          {/* Month navigation */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={previousMonth}
              className="p-2"
            >
              <ChevronLeft size={16} />
            </Button>
            
            <h3 className="font-semibold text-lg">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h3>
            
            <Button
              variant="outline"
              size="sm"
              onClick={nextMonth}
              className="p-2"
            >
              <ChevronRight size={16} />
            </Button>
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-0.5">
            {/* Day headers */}
            {dayNames.map(day => (
              <div key={day} className="p-1 text-center text-xs font-medium text-gray-500">
                {day}
              </div>
            ))}
            
            {/* Calendar days */}
            {days.map((day, index) => {
              if (!day) {
                return <div key={index} className="p-2" />;
              }
              
              const dateString = formatDate(day);
              const videoCount = videoCounts[dateString] || 0;
              const isToday = dateString === todayString;
              const isSelected = dateString === tempSelectedDate;
              const isPast = day < today && !isToday;
              
              return (
                <button
                  key={index}
                  onClick={() => handleDateClick(day)}
                  className={`
                    p-1 text-xs rounded transition-colors relative min-h-[32px] flex flex-col items-center justify-center hover:bg-blue-50 cursor-pointer
                    ${isPast ? 'text-gray-500' : 'text-gray-700'}
                    ${isToday ? 'bg-blue-100 border border-blue-500' : ''}
                    ${isSelected ? 'bg-blue-500 text-white' : ''}
                  `}
                >
                  <span className="text-xs">{day.getDate()}</span>
                  {videoCount > 0 && (
                    <div className="flex items-center space-x-0.5 mt-0.5">
                      <Video size={6} className="text-blue-600" />
                      <span className="text-xs text-blue-600 font-medium">{videoCount}</span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>



          {/* Legend */}
          <div className="flex items-center justify-center space-x-3 text-xs text-gray-600 pt-1 border-t flex-shrink-0">
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-blue-100 border border-blue-500 rounded"></div>
              <span>Today</span>
            </div>
            <div className="flex items-center space-x-1">
              <Video size={12} className="text-blue-600" />
              <span>Videos scheduled</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-2 pt-3 border-t flex-shrink-0">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveDate}
              disabled={!tempSelectedDate}
              className="flex items-center space-x-2"
            >
              <Save size={16} />
              <span>Save Date</span>
            </Button>
          </div>
        </div>

        {/* Selected Date Summary - Fixed Height Scrollable Section */}
        {tempSelectedDate && (
          <div className="mt-3 border-t pt-3 flex-shrink-0">
            <Card>
              <CardContent className="p-3">
                <div className="flex items-center space-x-2 mb-2">
                  <Clock size={14} className="text-blue-600" />
                  <span className="font-medium text-xs">
                    {new Date(tempSelectedDate + 'T00:00:00').toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>
                
                {(() => {
                  const videosOnDate = getVideosForDate(tempSelectedDate);
                  if (videosOnDate.length === 0) {
                    return (
                      <p className="text-sm text-gray-500">No videos scheduled for this date</p>
                    );
                  }
                  
                  return (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        {videosOnDate.length} video{videosOnDate.length > 1 ? 's' : ''} scheduled:
                      </p>
                      <div className="max-h-32 overflow-y-auto border rounded-md p-2 bg-gray-50">
                        <div className="space-y-1">
                          {videosOnDate.map((video) => {
                            const modules = getModulesForVideo(video);
                            return (
                              <div key={video.id} className="flex items-start space-x-2 text-xs py-1 bg-white rounded border-b border-gray-100 last:border-b-0 px-2">
                                <Video size={12} className="text-blue-600 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                  <span className="block break-words leading-tight">{video.title}</span>
                                  {modules.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {modules.map((module, idx) => (
                                        <span key={idx} className={`px-1.5 py-0.5 rounded text-xs border ${getModuleColor(module)} flex-shrink-0`}>
                                          {module}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}